import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Label,
  ModalHeader,
  Input,
  FormGroup,
} from "reactstrap";
import { Progress } from "reactstrap";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import DropZoneCustom from "./DropZoneCustom";
import SiteService from "services/Site";

const UploadMode = {
  FORM: "FORM",
  LOADING: "LOADING",
  UPLOADED: "UPLOADED",
};

const UploadExcel = ({
  title,
  isOpen,
  onClose,
  onStartUploadingFile,
  fileSupport,
  isRequiredSiteCode = false,
}) => {
  const { t } = useTranslation();

  const [file, setFile] = useState(null);
  const [siteCode, setSiteCode] = useState("");
  const [listSite, setListSite] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [mode, setMode] = useState(UploadMode.FORM);

  const onFileChange = file => {
    setFile(file);
  };

  const getListSite = async () => {
    if (!isRequiredSiteCode) return;

    const res = await SiteService.getListSite({
      page: 1,
      size: 9999,
    });

    if (res?.error) {
      setErrorMessage(`${res.error.title}: ${res.error.description}`);
      setListSite([]);
      return;
    }

    setListSite(res?.data || res || []);
  };

  const onFileUpload = async () => {
    if (isRequiredSiteCode && !siteCode) {
      setErrorMessage(t("Vui lòng chọn Schema Name"));
      setMode(UploadMode.FORM);
      return;
    }

    const formData = new FormData();
    formData.append("fileData", file);

    if (isRequiredSiteCode) {
      formData.append("siteCode", siteCode);
    }

    const response = await onStartUploadingFile(formData);

    if (response?.error) {
      setErrorMessage(`${response.error.title}: ${response.error.description}`);
    }

    setMode(UploadMode.UPLOADED);
  };

  useEffect(() => {
    if (isOpen) {
      getListSite();
    }
  }, [isOpen, isRequiredSiteCode]);

  useEffect(() => {
    if (mode === UploadMode.LOADING) {
      onFileUpload();
    }
  }, [mode]);

  const resetForm = () => {
    setFile(null);
    setSiteCode("");
    setMode(UploadMode.FORM);
    setErrorMessage(null);
  };

  const renderContent = () => {
    if (mode === UploadMode.LOADING) {
      return (
        <>
          <Label>Đang xử lý, xin vui lòng chờ trong giây lát...</Label>
          <div className="mt-1">
            <Progress color="primary" animated value="100" />
          </div>
        </>
      );
    }

    if (mode === UploadMode.UPLOADED) {
      return (
        <>
          {errorMessage ? (
            <Label className="text-danger">{errorMessage}</Label>
          ) : (
            <Label className="text-success">
              {t("Uploading Success Text")}
            </Label>
          )}

          <div className="d-flex justify-content-end mt-2">
            <Button color="primary" onClick={resetForm}>
              {t("Tiếp tục upload file")}
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        {isRequiredSiteCode && (
          <FormGroup>
            <Label>
              {t("SiteCode")} <span className="text-danger">*</span>
            </Label>

            <Input
              type="select"
              value={siteCode}
              onChange={e => setSiteCode(e.target.value)}
            >
              <option value="">{t("-- Chọn Site Code --")}</option>

              {listSite.map(item => (
                <option key={item.siteCode} value={item.siteCode}>
                  {`${item.siteCode} - ${item.siteName}`}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        <DropZoneCustom onChangeFile={onFileChange} />

        <div className="w-100 d-flex justify-content-end mt-1">
          <Label>
            <em>
              {t("Định dạng hỗ trợ")}: <b>{fileSupport || "*.xlsx"}</b>
            </em>
          </Label>
        </div>

        {errorMessage && (
          <div className="mt-1">
            <Label className="text-danger">{errorMessage}</Label>
          </div>
        )}

        <div className="d-flex justify-content-end mt-2">
          <Button
            color="primary"
            onClick={() => {
              setErrorMessage(null);
              setMode(UploadMode.LOADING);
            }}
            disabled={!file || (isRequiredSiteCode && !siteCode)}
            className="mx-2"
          >
            {t("Đồng ý")}
          </Button>

          <Button color="danger" onClick={onClose}>
            {t("Thoát")}
          </Button>
        </div>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClosed={onClose}>
      <ModalHeader>
        {title && typeof title === "string" ? title || t("Import file") : title}
      </ModalHeader>

      <div className="mt-3 p-3">{renderContent()}</div>
    </Modal>
  );
};

UploadExcel.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onStartUploadingFile: PropTypes.func,
  fileSupport: PropTypes.string,
  title: PropTypes.any,
  isRequiredSiteCode: PropTypes.bool,
};

export default UploadExcel;
